import { styled } from '@mui/material/styles';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';

// The chat drawer is a MUI modal stacked at zIndex 1600; the preview modal (and
// its dialogs) sit above it at 1700 — matching the chat header tooltips / dialogs.
const CHAT_PREVIEW_ZINDEX = 1700;

export const PreviewModal = styled(Modal)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: CHAT_PREVIEW_ZINDEX,
});

// While the thumbnail is loading the frame reserves a fixed A4-portrait area (90vh
// tall, width derived from the 210/297 aspect ratio, capped to 90vw) so the
// absolutely-positioned buttons sit at their final spots from the first render — the
// Open button top-center and the Close button in the top-right corner — instead of
// the frame collapsing to zero width and the Close button jumping in once the image
// loads. Once loaded the fixed sizing is dropped so the frame hugs the image's
// natural aspect ratio. No background is drawn during loading.
export const PreviewContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'loaded',
})<{ loaded?: boolean }>(({ loaded }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  outline: 'none',
  ...(loaded ? {} : { height: '90vh', aspectRatio: '210 / 297', maxWidth: '90vw' }),
}));

export const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: -16,
  right: -16,
  backgroundColor: theme.palette.common.white,
  boxShadow: theme.shadows[2],
  zIndex: 1,
  '&:hover': {
    backgroundColor: theme.palette.grey[100],
  },
}));

export const PreviewImage = styled('img')({
  maxWidth: '90vw',
  maxHeight: '90vh',
  display: 'block',
});

// `as typeof Button` restores the polymorphic `component` prop that `styled()`
// strips, so `<OpenButton component={RouterLink} to={...} />` type-checks.
export const OpenButton = styled(Button)(({ theme }) => ({
  position: 'absolute',
  top: -16,
  left: '50%',
  transform: 'translateX(-50%)',
  boxShadow: theme.shadows[2],
  zIndex: 1,
})) as typeof Button;
